/**
 * What the writer sees of a source, unit by unit (table U in the part 10 log): every character of text as the
 * character it shows (an escape or entity as one unit), every character of markup as itself. The input rules
 * (`rewrite.ts`) compare the units of a source before and after an edit, so "text stays text" and "edit what you see"
 * (principles 2 and 3) are checked on what the writer sees, not on how it is written.
 */
import { chapterModel } from '@cockatrice/core';
import type { Block, ChapterModel, Inline, ParsedChapter, TextPiece } from '@cockatrice/core';

/**
 * Text (U1, U2), markup inside an inline element or outside every one (U3), or a value zone (U4): a link's destination
 * or title or a note reference's label (`value`), or a note definition's label or a link definition's destination or
 * title (`label`).
 */
export type UnitKind = 'text' | 'inline' | 'block' | 'value' | 'label';

/** One unit: what it shows and the source it comes from. */
export interface Unit {
  readonly kind: UnitKind;
  /** What the writer sees: a markup unit shows itself, a text unit its character(s). */
  readonly show: string;
  readonly from: number;
  readonly to: number;
  /**
   * A heading's `#` run, a list mark or a quote mark (table P, P3); `opener` when typing after it on an otherwise empty
   * line gives the block content (P5): a heading's, a list item's or a note's mark.
   */
  readonly mark: 'mark' | 'opener' | null;
  /**
   * How a text unit is written: a literal character, a reference (an escape or entity, U2), or a character of a delimiter
   * run's unmatched part, written as it is (U1); null for markup.
   */
  readonly origin: 'literal' | 'reference' | 'delimiter' | null;
}

/** Nodes whose characters are a value the writer edits as written (U4, P2). */
const VALUE_NODES: ReadonlySet<string> = new Set(['URL', 'LinkTitle', 'FootnoteLabel']);

/**
 * Visits every inline element, depth first: `text` for each text piece, `element` for the markup of each other element.
 * A soft break's markup is its line end: the next line's container prefix it spans is block markup.
 */
function walkInline(items: readonly Inline[], source: string, visit: { text: (piece: TextPiece) => void; element: (from: number, to: number) => void }): void {
  for (const item of items) {
    if (item.kind === 'text') {
      for (const piece of item.pieces) visit.text(piece);
      continue;
    }
    visit.element(item.from, item.kind === 'soft-break' ? source.indexOf('\n', item.from) + 1 : item.to);
    if (item.kind === 'emphasis' || item.kind === 'strong' || item.kind === 'small-caps' || item.kind === 'link' || item.kind === 'image' || item.kind === 'brackets') {
      walkInline(item.children, source, visit);
    }
  }
}

function walkBlocks(blocks: readonly Block[], visit: (content: readonly Inline[]) => void): void {
  for (const block of blocks) {
    switch (block.kind) {
      case 'paragraph':
      case 'heading':
      case 'centered':
        visit(block.content);
        break;
      case 'quote':
      case 'footnote':
        walkBlocks(block.blocks, visit);
        break;
      case 'list':
        for (const item of block.items) walkBlocks(item, visit);
        break;
      case 'scene-break':
        break;
    }
  }
}

/** Whether `code` starts a UTF-16 surrogate pair. */
function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

/** What a character no text piece holds is, as one code: its kind and, for block markup, its mark (0: plain block markup). */
const INLINE = 1;
const VALUE = 2;
const LABEL = 3;
const OPENER = 4;
const MARK = 5;

/** A markup unit for character `i` of `source`, from its code. */
function markupUnit(source: string, i: number, code: number): Unit {
  const unit = { show: source.charAt(i), from: i, to: i + 1, origin: null };
  switch (code) {
    case INLINE:
      return { ...unit, kind: 'inline', mark: null };
    case VALUE:
      return { ...unit, kind: 'value', mark: null };
    case LABEL:
      return { ...unit, kind: 'label', mark: null };
    case OPENER:
      return { ...unit, kind: 'block', mark: 'opener' };
    case MARK:
      return { ...unit, kind: 'block', mark: 'mark' };
    default:
      return { ...unit, kind: 'block', mark: null };
  }
}

/**
 * The units of a parsed source, in source order, covering every character once (table U). A literal piece gives one
 * unit per code point, so no edit splits a surrogate pair; an escape or entity gives one unit (U2). `model` is the
 * source's model, when the caller has it already. Pure; linear in the source.
 */
export function unitsOf(parsed: ParsedChapter, model: ChapterModel = chapterModel(parsed)): Unit[] {
  const source = parsed.text;
  /** What each character is when no text piece holds it. */
  const codes = new Uint8Array(source.length);
  /** The text pieces, in source order. */
  const pieces: TextPiece[] = [];
  walkBlocks(model.blocks, (content) => {
    walkInline(content, source, {
      element: (from, to) => {
        codes.fill(INLINE, from, to);
      },
      text: (piece) => {
        pieces.push(piece);
      },
    });
  });
  parsed.tree.iterate({
    enter: (node) => {
      if (VALUE_NODES.has(node.name)) {
        const parent = node.node.parent?.name;
        codes.fill(parent === 'FootnoteDefinition' || parent === 'LinkReference' ? LABEL : VALUE, node.from, node.to);
      } else if (node.name === 'HeaderMark' || node.name === 'ListMark') {
        codes.fill(OPENER, node.from, node.to);
      } else if (node.name === 'QuoteMark') {
        codes.fill(MARK, node.from, node.to);
      } else if (node.name === 'FootnoteMark' && node.node.parent?.name === 'FootnoteDefinition' && source.charAt(node.from) === ']') {
        codes.fill(OPENER, node.from, node.to);
      }
    },
  });
  const units: Unit[] = [];
  const next = pieces.values();
  let piece = next.next();
  let skipTo = 0;
  for (const [i, code] of codes.entries()) {
    if (i < skipTo) continue;
    while (!piece.done && piece.value.to <= i) piece = next.next();
    if (piece.done || piece.value.from > i) {
      units.push(markupUnit(source, i, code));
      continue;
    }
    const { value } = piece;
    if (value.origin === 'escape' || value.origin === 'entity') {
      units.push({ kind: 'text', show: value.text, from: value.from, to: value.to, mark: null, origin: 'reference' });
      skipTo = value.to;
      continue;
    }
    const step = isHighSurrogate(source.charCodeAt(i)) && i + 1 < value.to ? 2 : 1;
    units.push({ kind: 'text', show: source.slice(i, i + step), from: i, to: i + step, mark: null, origin: value.origin });
    skipTo = i + step;
  }
  return units;
}
